import { CSSProperties, ReactNode, useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  ConfigProvider,
  Dropdown,
  Empty,
  Flex,
  Input,
  InputNumber,
  Result,
  Segmented,
  Select,
  Skeleton,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { ApiService } from "api/api.service";
import DashboardLayout from "layout/DashboardLayout";
import PageTitle from "components/PageTitle";
import { AddGroupModal } from "components/TaskTemplate/AddGroupModal";
import { AddTaskItemModal } from "components/TaskTemplate/AddTaskItemModal";
import {
  TbAlertTriangle,
  TbArrowNarrowRight,
  TbX,
  TbChevronDown,
  TbChevronRight,
  TbCopy,
  TbDots,
  TbGripVertical,
  TbPencil,
  TbPlus,
  TbSearch,
  TbTrash,
  TbClock,
  TbUserCircle,
} from "react-icons/tb";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MemberKind, MemberRole, MemberTarget, TaskItemMember } from "dtos/tasks/task-template.dto";
import { T_MongoId } from "types/_utils.types";
import { accentTag, neutralTag, taskUi } from "components/TaskBoard/design";
import {
  countSubtasks,
  CriteriaTone,
  flattenTemplate,
  indexTasks,
  isMemberResolved,
  memberFor,
  resolveActivation,
  ResolvedDependency,
  resolveCompletion,
  subtaskNodes,
  TaskNode,
  validateTemplate,
} from "./taskTemplateGraph";
import { TEAM_ROLES } from "pages/ApplicationItem/TeamSection";
import { getInitials, MemberPicker, MemberSelection } from "./MemberPicker";
import { toCapitalCase } from "utils/functions";
import { StageIcon } from "components/Stages/picker";
import { resolveStageColor } from "components/Stages/constants";
import { Deadline, describeDeadline } from "./deadline";

const COLUMN_COUNT = 7;

const GROUP_BG = "#f4f4f8";
const GRID_LINE = "#e8e8ee";

const headerCell: CSSProperties = {
  height: 34,
  background: taskUi.subtle,
  borderBottom: `1px solid ${GRID_LINE}`,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: taskUi.textFaint,
};

type TemplateRow =
  | {
      kind: "group";
      key: string;
      groupId: string;
      name: string;
      collapsed: boolean;
      rootCount: number;
      subCount: number;
    }
  | { kind: "empty"; key: string; message: string }
  | { kind: "task"; key: string; node: TaskNode };

const ROLE_ORDER: MemberRole[] = [MemberRole.OWNER, MemberRole.VERIFIER, MemberRole.ACTOR];

const ROLE_LABEL: Record<MemberRole, string> = {
  [MemberRole.OWNER]: "Owner",
  [MemberRole.VERIFIER]: "Verifier",
  [MemberRole.ACTOR]: "Actor",
};

const KIND_LABEL: Record<ResolvedDependency["kind"], string> = {
  previous: "Previous task",
  parent: "Parent task",
  outcome: "Outcome of",
};

const externalTag = { ...neutralTag, color: "#97551f", background: "#fdf3e7", borderColor: "#ebd8ac" };

const NOTE_COLOR: Record<CriteriaTone, string> = {
  neutral: taskUi.textFaint,
  accent: taskUi.textFaint,
  warning: "#b45309",
  error: "#b42318",
};

const MemberDot = ({ role, member, label }: { role: MemberRole; member?: TaskItemMember; label: string }) => {
  const external = member?.kind === MemberKind.EXTERNAL;
  const isUser = !external && member?.target === MemberTarget.INDIVIDUAL && Boolean(member?.assigneeId);
  const resolved = isMemberResolved(member);

  const tone =
    !member || !resolved
      ? { background: "#ffffff", color: "#b4b4be", border: "1px dashed #c6c6d0" }
      : external
        ? { background: "#fdf3e7", color: "#97551f", border: "1px solid #ebd8ac" }
        : {
            background: isUser ? taskUi.indigoSoft : taskUi.purpleSoft,
            color: isUser ? taskUi.indigo : taskUi.purple,
            border: `1px solid ${isUser ? taskUi.indigoLine : taskUi.purpleLine}`,
          };

  const Icon =
    member?.target === MemberTarget.ROLE ? TEAM_ROLES.find((r) => r.value === member?.assigneeRole)?.icon : undefined;
  const initials =
    member?.target === MemberTarget.INDIVIDUAL && (member?.assigneeId as any)?.name
      ? getInitials((member?.assigneeId as any).name)
      : undefined;

  return (
    <Tooltip title={`${ROLE_LABEL[role]} — ${label}`}>
      <span
        style={{
          ...tone,
          width: 22,
          height: 22,
          borderRadius: "50%",
          fontSize: 10,
          fontWeight: 650,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: -5,
        }}
      >
        {external ? <TbUserCircle size={14} /> : Icon ? <Icon size={12} /> : (initials ?? label.charAt(0))}
      </span>
    </Tooltip>
  );
};

const SectionHeading = ({ title, action }: { title: string; action?: ReactNode }) => (
  <div className="mb-2.5 flex items-center">
    <span
      style={{
        fontSize: 11,
        fontWeight: 650,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: taskUi.textFaint,
        flexGrow: 1,
      }}
    >
      {title}
    </span>
    {action}
  </div>
);

const ToneColors = {
  error: "#b42318",
  muted: "#a4a4b0",
  highlight: "#2563eb",
  default: taskUi.textFaint,
} as const;

type Tones = keyof typeof ToneColors;

const MonoCode = ({ value, tone }: { value?: string; tone?: Tones }) => (
  <span
    style={{
      fontSize: 10.5,
      fontFamily: "ui-monospace, Menlo, monospace",
      color: ToneColors[tone || "default"] ?? ToneColors["default"],
      flexShrink: 0,
    }}
  >
    {value ?? "??"}
  </span>
);

const Info = ({
  tasks = 0,
  subtasks = 0,
  groups = 0,
  className,
  style = {},
}: {
  tasks?: number;
  subtasks?: number;
  groups?: number;
  className?: string;
  style?: CSSProperties;
}) => {
  return (
    <span
      hidden={!tasks && !subtasks}
      style={{ fontSize: 12, color: taskUi.textFaint, fontVariantNumeric: "tabular-nums", ...style }}
      className={className}
    >
      {tasks > 0 ? tasks : ""}
      {subtasks > 0 && tasks > 0 ? "+" : ""}
      {subtasks > 0 ? subtasks : ""} tasks
    </span>
  );
};

function TaskTemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [sp, setSp] = useSearchParams();

  const templateQuery = ApiService.taskTemplate.useGetById(id);
  const template = templateQuery.data;

  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(() => sp.get("task") ?? undefined);
  const [inspectorTab, setInspectorTab] = useState("criteria");
  const [editingRole, setEditingRole] = useState<MemberRole | null>(null);
  const [search, setSearch] = useState("");
  const [missingRole, setMissingRole] = useState<MemberRole | undefined>();
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [addTaskState, setAddTaskState] = useState<{
    groupId: string;
    parent: string | null;
    previous: string | null;
  } | null>(null);
  const [stageEditEnabled, setStageEditEnabled] = useState(false);

  const orgQuery = ApiService.organisation.useGetAllOrganisations();
  const orgCode = orgQuery.data?.find((o) => String(o._id) === String(template?.organisation))?.code;
  const stagesQuery = ApiService.organisation.useGetAllStages(orgCode, { resource: template?.resource });
  const stages = stagesQuery.data ?? [];

  const directory = ApiService.user.useGetPaginated({ page: 1, limit: 100, search: "", role: [] });
  const updateMember = ApiService.taskTemplate.useUpdateTaskMembers();
  const updateItem = ApiService.taskTemplate.useUpdateTaskDetails();
  // const removeMember = ApiService.taskTemplate.useRemoveTaskMember(id);
  const [deadlineEdit, setDeadlineEdit] = useState(false);
  const [deadlineDraft, setDeadlineDraft] = useState<Deadline>({ days: 3, businessDays: true });

  useEffect(() => setDeadlineEdit(false), [selectedTaskId]);

  const startDeadlineEdit = (task: TaskNode["task"]) => {
    setDeadlineDraft(task.deadline ?? { days: 3, businessDays: true });
    setDeadlineEdit(true);
  };

  const selectTask = (task: TaskNode["task"]) => {
    setSelectedTaskId(task._id);
    setSp((prev) => ({ ...prev, task: task._id }));
  };

  const peopleById = useMemo(
    () => new Map((directory.data?.result.data ?? []).map((user) => [String(user._id), user.name])),
    [directory.data],
  );

  const memberLabel = (member?: TaskItemMember) => {
    if (!member) return "Not on this task";

    if (member.target === MemberTarget.ROLE) {
      if (member.kind === MemberKind.EXTERNAL) return "The client";
      if (!member.assigneeRole) return "Role not set";
      return TEAM_ROLES.find((option) => option.value === member.assigneeRole)?.label ?? member.assigneeRole;
    }

    if (!member.assigneeId) return "Unassigned";

    return peopleById.get(String((member.assigneeId as any)._id || member.assigneeId)) ?? "Assigned";
  };

  // const memberIcon = (member?: TaskItemMember) => {
  //   if (!member) return null;
  //   if (member.kind === MemberKind.EXTERNAL) return null;
  //   if (member.target === MemberTarget.ROLE) {

  //   }
  // }

  const nodes = useMemo(() => flattenTemplate(template?.groups ?? []), [template]);
  const index = useMemo(() => indexTasks(nodes), [nodes]);
  const issues = useMemo(() => validateTemplate(nodes, index), [nodes, index]);

  const issuesByTask = useMemo(() => {
    const map = new Map<string, typeof issues>();
    issues.forEach((issue) => map.set(issue.taskId, [...(map.get(issue.taskId) ?? []), issue]));
    return map;
  }, [issues]);

  // const errorCount = issues.filter((issue) => issue.severity === "error").length;

  const visibleIds = useMemo(() => {
    const term = search.trim().toLowerCase();

    const matches = (node: TaskNode) => {
      if (term && !`${node.seq} ${node.task.name} ${node.task.code}`.toLowerCase().includes(term)) return false;
      if (missingRole && isMemberResolved(memberFor(node.task, missingRole))) return false;
      if (onlyIssues && !issuesByTask.has(String(node.task._id))) return false;
      return true;
    };

    const kept = new Set<string>();

    nodes.forEach((node) => {
      if (!matches(node)) return;
      kept.add(String(node.task._id));
      if (node.parentId) kept.add(node.parentId);
    });

    return kept;
  }, [nodes, search, missingRole, onlyIssues, issuesByTask]);

  const selected = selectedTaskId ? index.get(selectedTaskId) : undefined;

  const copyFromFor = (role: MemberRole) => {
    if (!selected?.previousId) return undefined;
    const previous = index.get(selected.previousId);
    const member = previous ? memberFor(previous.task, role) : undefined;
    return previous && member ? { code: previous.seq, member } : undefined;
  };

  const handleAddTask = (groupId: string, defaults?: { parent?: T_MongoId | null; previous?: T_MongoId | null }) => {
    if (!template) return;

    const group = template.groups.find((g) => String(g._id) === groupId);
    if (!group) return;

    const parent = defaults?.parent ? String(defaults.parent) : null;

    let previous: string | null = null;

    if (defaults?.previous) {
      previous = String(defaults.previous);
    } else if (parent) {
      const parentTask = group.tasks.find((t) => String(t._id) === parent);
      const subtasks = parentTask?.subtasks ?? [];
      previous = subtasks.length > 0 ? String(subtasks[subtasks.length - 1]._id) : null;
    } else {
      previous = group.tasks.length > 0 ? String(group.tasks[group.tasks.length - 1]._id) : null;
    }

    setAddTaskState({ groupId, parent, previous });
  };

  const onLinkStage = (taskId: T_MongoId, stageId: T_MongoId | null) => {
    updateItem.mutate(
      {
        taskId,
        relatedStage: stageId,
      },
      {
        onSuccess: () => {
          setStageEditEnabled(false);
          void templateQuery.refetch();
          void message.success("Stage linked to task item");
        },
      },
    );
  };

  const onChangeDeadline = (task: TaskNode["task"], deadline: NonNullable<TaskNode["task"]["deadline"]> | null) => {
    if (task.deadline?.days === deadline?.days && task.deadline?.businessDays === deadline?.businessDays) return;

    let successMessage = "Task deadline modified";
    if (!deadline) successMessage = "Task deadline removed";
    else if (!task.deadline) successMessage = "Task deadline added";
    else if (task.deadline.days > deadline.days) successMessage = "Task deadline reduced";
    else if (task.deadline.days < deadline.days) successMessage = "Task deadline extended";

    updateItem.mutate(
      { taskId: task._id, deadline },
      {
        onSuccess: () => {
          void templateQuery.refetch();
          void message.success(successMessage);
        },
      },
    );
  };

  const toggleGroup = (groupId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });

  const saveDetails = (taskId: T_MongoId, patch: { name?: string; description?: string }) => {
    updateItem.mutate(
      { taskId, ...patch },
      {
        onSuccess: () => {
          void templateQuery.refetch();
          void message.success("Task updated");
        },
        onError: () => void message.error("Could not update the task"),
      },
    );
  };

  const saveMember = (role: MemberRole, selection: MemberSelection | null) => {
    if (!selected) return;

    const taskId = String(selected.task._id);

    const done = (text: string) => {
      setEditingRole(null);
      void templateQuery.refetch();
      void message.success(text);
    };

    if (!selection) {
      message.error("Can not unassign a member");
      // removeMember.mutate(
      //   { taskId, role },
      //   { onSuccess: () => done("Member removed"), onError: () => void message.error("Could not remove the member") },
      // );
      return;
    }

    const member: TaskItemMember = {
      role,
      kind: selection.kind,
      target: selection.target,
      assigneeRole:
        selection.target === MemberTarget.ROLE && selection.kind === MemberKind.INTERNAL
          ? selection.assigneeRole
          : undefined,
      assigneeId:
        selection.target === MemberTarget.INDIVIDUAL
          ? (selection.assigneeId as any)._id || (selection.assigneeId as unknown as T_MongoId)
          : undefined,
    };

    updateMember.mutate(
      { taskId, member },
      { onSuccess: () => done(`${toCapitalCase(member.role)} assigned to task ${selected.task.code}`) },
    );
  };

  if (templateQuery.isLoading) {
    return (
      <DashboardLayout>
        <div className="mx-auto w-full max-w-6xl" style={{ padding: 24 }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </DashboardLayout>
    );
  }

  if (templateQuery.isError || !template) {
    return (
      <DashboardLayout>
        <Result
          status="warning"
          title="Template not found"
          subTitle="This template may have been deleted, or the URL is incorrect."
          extra={
            <Space>
              <Button onClick={() => navigate("/dashboard/operations/tasks")}>Back to templates</Button>
              <Button type="primary" onClick={() => void templateQuery.refetch()} loading={templateQuery.isFetching}>
                Retry
              </Button>
            </Space>
          }
        />
      </DashboardLayout>
    );
  }

  const activeGroup = addTaskState ? template.groups.find((g) => String(g._id) === addTaskState.groupId) : undefined;

  const TaskLinkRow = ({ node, trailing }: { node?: TaskNode; trailing?: ReactNode }) => (
    <button
      type="button"
      disabled={!node}
      onClick={() => node && selectTask(node.task)}
      className="flex w-full items-center gap-2 border border-solid bg-white text-left"
      style={{
        padding: "7px 10px",
        borderRadius: 6,
        borderColor: node ? "#e8e8ee" : "#f5c6c0",
        cursor: node ? "pointer" : "default",
      }}
    >
      <MonoCode value={node?.seq} tone={node ? "muted" : "error"} />
      <Typography.Text style={{ fontSize: 12.5, flexGrow: 1, color: node ? taskUi.text : "#b42318" }} ellipsis>
        {node ? node.task.name : "Unknown task"}
      </Typography.Text>
      {trailing}
    </button>
  );

  const renderCriteria = () => {
    if (!selected) return null;

    const activation = resolveActivation(selected, index);
    const completion = resolveCompletion(selected, index);

    return (
      <div className="flex flex-col gap-5" style={{ padding: "14px 16px" }}>
        <div>
          <SectionHeading
            title="Activates when"
            action={
              activation && activation.total > 1 ? (
                <Tag bordered style={neutralTag}>
                  {activation.mode === "all"
                    ? `all ${activation.total}`
                    : `any ${activation.required} of ${activation.total}`}
                </Tag>
              ) : undefined
            }
          />

          {!activation ? (
            <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint }}>
              No condition — starts with its group
            </Typography.Text>
          ) : (
            <div className="flex flex-col gap-1.5">
              {activation.items.map((item, i) => (
                <div key={i} className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    <Typography.Text style={{ fontSize: 11, color: taskUi.textFaint }}>
                      {KIND_LABEL[item.kind]}
                    </Typography.Text>
                    <TbArrowNarrowRight size={12} color="#c6c6d0" />
                    <Typography.Text style={{ fontSize: 11, color: taskUi.textFaint }}>
                      {item.requirement}
                    </Typography.Text>
                  </div>

                  {item.node || item.tone === "error" ? (
                    <TaskLinkRow
                      node={item.node}
                      trailing={
                        item.outcomeId ? (
                          <Tag bordered style={accentTag}>
                            outcome {item.outcomeId.slice(-6)}
                          </Tag>
                        ) : undefined
                      }
                    />
                  ) : null}

                  {item.note && (
                    <Typography.Text style={{ fontSize: 11.5, color: NOTE_COLOR[item.tone] }}>
                      {item.note}
                    </Typography.Text>
                  )}
                </div>
              ))}

              {activation.nestedGroups > 0 && (
                <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint }}>
                  Flattened from {activation.nestedGroups} nested condition group
                  {activation.nestedGroups === 1 ? "" : "s"}
                </Typography.Text>
              )}
            </div>
          )}
        </div>

        <div>
          <SectionHeading
            title="Completes when"
            action={
              completion.total > 1 ? (
                <Tag bordered style={neutralTag}>
                  {completion.mode === "all"
                    ? `all ${completion.total}`
                    : `any ${completion.required} of ${completion.total}`}
                </Tag>
              ) : undefined
            }
          />

          {completion.total === 0 ? (
            <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint }}>
              No rule — completed by hand on the board
            </Typography.Text>
          ) : (
            <div className="flex flex-col gap-4">
              {completion.subtaskGate && (
                <div className="flex flex-col gap-1.5">
                  <Typography.Text style={{ fontSize: 11, color: taskUi.textFaint }}>
                    Every sub-task below must complete
                  </Typography.Text>

                  {completion.subtasks.length === 0 ? (
                    <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint }}>
                      No sub-tasks added yet
                    </Typography.Text>
                  ) : (
                    completion.subtasks.map((sub) => <TaskLinkRow key={String(sub.task._id)} node={sub} />)
                  )}
                </div>
              )}

              {completion.memberActions.map((action, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                  <Typography.Text style={{ fontSize: 11, color: taskUi.textFaint }}>
                    {ROLE_LABEL[action.role]} records an outcome
                  </Typography.Text>

                  <div
                    className="flex items-center gap-2 border border-solid"
                    style={{
                      padding: "7px 10px",
                      borderRadius: 6,
                      borderColor: action.tone === "error" ? "#f5c6c0" : "#e8e8ee",
                    }}
                  >
                    <MemberDot role={action.role} member={action.member} label={memberLabel(action.member)} />
                    <Typography.Text style={{ fontSize: 12.5, flexGrow: 1, marginLeft: 5 }}>
                      {action.member ? memberLabel(action.member) : `No ${ROLE_LABEL[action.role].toLowerCase()} set`}
                    </Typography.Text>
                    <Tag bordered style={accentTag}>
                      outcome {action.outcomeId.slice(-6)}
                    </Tag>
                  </div>

                  {action.note && (
                    <Typography.Text style={{ fontSize: 11.5, color: NOTE_COLOR[action.tone] }}>
                      {action.note}
                    </Typography.Text>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSubtasks = (siblings: TaskNode[]) => {
    if (!selected) return null;

    return (
      <div className="flex flex-col gap-1.5" style={{ padding: "14px 16px" }}>
        {siblings.length === 0 ? (
          <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint }}>
            This task has no sub-tasks
          </Typography.Text>
        ) : (
          siblings.map((sub) => {
            const subId = String(sub.task._id);
            const subIssues = issuesByTask.get(subId) ?? [];
            const hasError = subIssues.some((issue) => issue.severity === "error");

            return (
              <TaskLinkRow
                key={subId}
                node={sub}
                trailing={
                  <span className="flex items-center gap-2">
                    {hasError && <TbAlertTriangle size={13} color="#b42318" />}
                    {ROLE_ORDER.map((role) => (
                      <MemberDot
                        key={role}
                        role={role}
                        member={memberFor(sub.task, role)}
                        label={memberLabel(memberFor(sub.task, role))}
                      />
                    ))}
                  </span>
                }
              />
            );
          })
        )}

        <Button
          type="text"
          icon={<TbPlus size={14} />}
          onClick={() => handleAddTask(selected.groupId, { parent: selected.parentId ?? selected.task._id })}
          style={{ color: "#6b6b76", fontSize: 12.5, alignSelf: "flex-start", paddingLeft: 4 }}
        >
          Add sub-task
        </Button>
      </div>
    );
  };

  const renderInspector = () => {
    if (!selected) {
      return (
        <div className="flex h-full items-center justify-center" style={{ padding: 24 }}>
          <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint, textAlign: "center" }}>
            Select a task to see its members, dependencies and sub-tasks
          </Typography.Text>
        </div>
      );
    }

    const task = selected.task;
    const rowIssues = issuesByTask.get(String(task._id)) ?? [];
    const parent = selected.parentId ? index.get(selected.parentId) : undefined;
    const siblings = parent ? subtaskNodes(parent, index) : subtaskNodes(selected, index);
    const relatedStage = stages.find((s) => task.relatedStage === s._id);

    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex-shrink-0">
          <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${taskUi.hairline}` }}>
            <div className="mb-1.5 flex items-center gap-2">
              <MonoCode value={task.code} />
            </div>

            <Typography.Title
              key={`name-${String(task._id)}`}
              level={5}
              style={{ fontSize: 15.5, lineHeight: 1.3, marginBottom: 6 }}
              // className="line-clamp-1"
              editable={{
                tooltip: "Rename task",
                icon: <TbPencil size={14} className="text-gray-500" />,
                maxLength: 120,
                autoSize: { minRows: 1, maxRows: 3 },
                onChange: (value) => {
                  const next = value.trim();
                  if (!next) return void message.error("A task needs a name");
                  if (next === task.name) return;
                  saveDetails(task._id, { name: next });
                },
              }}
            >
              {task.name}
            </Typography.Title>

            <Typography.Paragraph
              key={`desc-${String(task._id)}`}
              style={{
                fontSize: 12.5,
                color: task.description ? "#6b6b76" : taskUi.textFaint,
                lineHeight: 1.45,
                marginBottom: 0,
                fontStyle: task.description ? undefined : "italic",
              }}
              editable={{
                tooltip: "Edit description",
                icon: <TbPencil size={14} className="text-gray-500" />,
                maxLength: 500,
                autoSize: { minRows: 2, maxRows: 6 },
                text: task.description ?? "",
                onChange: (value) => {
                  const next = value.trim();
                  if (next === (task.description ?? "")) return;
                  saveDetails(task._id, { description: next });
                },
              }}
            >
              {task.description || "Add a description"}
            </Typography.Paragraph>
          </div>
          {rowIssues.length > 0 && (
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${taskUi.hairline}`, background: "#fef8f7" }}>
              {rowIssues.map((issue, i) => (
                <div key={i} className="flex gap-2" style={{ marginTop: i === 0 ? 0 : 8 }}>
                  <TbAlertTriangle
                    size={14}
                    color={issue.severity === "error" ? "#b42318" : "#b45309"}
                    style={{ flexShrink: 0, marginTop: 1 }}
                  />
                  <Typography.Text style={{ fontSize: 12, color: issue.severity === "error" ? "#7f2a21" : "#7c4a0a" }}>
                    {issue.message}
                  </Typography.Text>
                </div>
              ))}
            </div>
          )}
          <div style={{ padding: "14px 16px", borderBottom: `1px solid ${taskUi.hairline}` }}>
            <SectionHeading title="Members" />
            {ROLE_ORDER.map((role) => {
              const member = memberFor(task, role);

              return (
                <div key={role} className="mb-2.5 flex items-center gap-2.5 last:mb-0">
                  <span style={{ width: 58, fontSize: 12, color: taskUi.textFaint }}>{ROLE_LABEL[role]}</span>
                  <MemberDot role={role} member={member} label={memberLabel(member)} />
                  <Typography.Text
                    style={{ fontSize: 13, color: member ? taskUi.text : taskUi.textFaint, marginLeft: 5, flexGrow: 1 }}
                    ellipsis
                  >
                    {memberLabel(member)}
                  </Typography.Text>

                  {member?.kind === MemberKind.EXTERNAL && (
                    <Tag bordered style={externalTag}>
                      external
                    </Tag>
                  )}
                  {member?.target === MemberTarget.ROLE && member.kind !== MemberKind.EXTERNAL && (
                    <Tag bordered style={neutralTag}>
                      team role
                    </Tag>
                  )}

                  <MemberPicker
                    role={role}
                    member={member}
                    open={editingRole === role}
                    onOpenChange={(open) => setEditingRole(open ? role : null)}
                    onPick={(selection) => saveMember(role, selection)}
                    onClear={() => saveMember(role, null)}
                    saving={updateMember.isPending}
                    copyFrom={copyFromFor(role)}
                  >
                    <Button
                      type="text"
                      size="small"
                      aria-label={
                        member ? `Edit ${ROLE_LABEL[role].toLowerCase()}` : `Assign ${ROLE_LABEL[role].toLowerCase()}`
                      }
                      icon={member ? <TbPencil size={14} /> : <TbPlus size={14} />}
                      style={{ color: "#9a9aa5" }}
                    />
                  </MemberPicker>
                </div>
              );
            })}
          </div>
          <div style={{ padding: "14px 16px", borderBottom: `1px solid ${taskUi.hairline}` }}>
            <SectionHeading title="Stage" />
            <Flex gap={10} className="items-center">
              {stageEditEnabled ? (
                <Select
                  size="small"
                  suffixIcon={null}
                  allowClear
                  placeholder={`Select ${template.resource} stage`}
                  defaultValue={relatedStage?._id}
                  popupMatchSelectWidth={200}
                  onChange={(value) => onLinkStage(selected.task._id, value ?? null)}
                  options={stages.map((stage) => ({
                    value: stage._id,
                    label: (
                      <span>
                        {
                          <StageIcon
                            className="inline mr-1"
                            iconTag={stage.icon}
                            color={resolveStageColor(stage.color).text}
                          />
                        }
                        <Typography.Text className="text-xs">{stage.label}</Typography.Text>
                      </span>
                    ),
                  }))}
                />
              ) : relatedStage ? (
                <Tag bordered style={neutralTag}>
                  {<StageIcon iconTag={relatedStage.icon} color={resolveStageColor(relatedStage.color).text} />}
                  {relatedStage.label}
                </Tag>
              ) : (
                <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint }}>Not linked</Typography.Text>
              )}
              <Button
                type="text"
                size="small"
                className="ml-auto"
                aria-label="Link stage with task"
                icon={stageEditEnabled ? <TbX /> : <TbPencil size={14} />}
                style={{ color: "#9a9aa5" }}
                onClick={() => setStageEditEnabled((prev) => !prev)}
              />
            </Flex>
          </div>
        </div>
        {selected.depth === 0 && (
          <div style={{ padding: "14px 16px", borderBottom: `1px solid ${taskUi.hairline}` }}>
            <SectionHeading title="Deadline" />

            {deadlineEdit ? (
              <div className="flex flex-col gap-2.5">
                <Flex gap={8} className="items-center">
                  <InputNumber
                    autoFocus
                    size="small"
                    min={1}
                    max={365}
                    style={{ width: 72 }}
                    value={deadlineDraft.days}
                    onChange={(value) => setDeadlineDraft((prev) => ({ ...prev, days: value ?? 1 }))}
                    onPressEnter={() => {
                      onChangeDeadline(task, deadlineDraft);
                      setDeadlineEdit(false);
                    }}
                  />

                  <Segmented
                    size="small"
                    value={deadlineDraft.businessDays ? "business" : "calendar"}
                    onChange={(value) => setDeadlineDraft((prev) => ({ ...prev, businessDays: value === "business" }))}
                    options={[
                      { value: "business", label: "Working" },
                      { value: "calendar", label: "Calendar" },
                    ]}
                  />
                </Flex>

                <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint }}>
                  Counted from the moment this task becomes active.
                </Typography.Text>

                <Flex gap={8} className="items-center">
                  <Button
                    type="primary"
                    size="small"
                    loading={updateItem.isPending}
                    onClick={() => {
                      onChangeDeadline(task, deadlineDraft);
                      setDeadlineEdit(false);
                    }}
                  >
                    Save
                  </Button>
                  <Button size="small" onClick={() => setDeadlineEdit(false)}>
                    Cancel
                  </Button>

                  {task.deadline && (
                    <Button
                      type="text"
                      size="small"
                      danger
                      className="ml-auto"
                      onClick={() => {
                        onChangeDeadline(task, null);
                        setDeadlineEdit(false);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Flex>
              </div>
            ) : (
              <Flex gap={10} className="items-center">
                {task.deadline ? (
                  <Tooltip title={describeDeadline(task.deadline)}>
                    <Tag bordered style={neutralTag}>
                      <TbClock size={12} />
                      {task.deadline.days} {task.deadline.businessDays ? "working" : "calendar"} day
                      {task.deadline.days === 1 ? "" : "s"}
                    </Tag>
                  </Tooltip>
                ) : (
                  <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint }}>Not set</Typography.Text>
                )}

                <Button
                  type="text"
                  size="small"
                  className="ml-auto"
                  aria-label={task.deadline ? "Edit deadline" : "Add a deadline"}
                  icon={task.deadline ? <TbPencil size={14} /> : <TbPlus size={14} />}
                  style={{ color: "#9a9aa5" }}
                  onClick={() => startDeadlineEdit(task)}
                />
              </Flex>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1">
          <Tabs
            activeKey={inspectorTab}
            onChange={setInspectorTab}
            tabBarStyle={{ paddingInline: 16, marginBottom: 0 }}
            style={{ height: "100%" }}
            items={[
              {
                key: "criteria",
                label: "Lifecycle",
                children: (
                  <div className="overflow-y-auto" style={{ maxHeight: "100%" }}>
                    {renderCriteria()}
                  </div>
                ),
              },
              {
                key: "subtasks",
                label: "Subtasks",
                // label: (
                //   <span className="flex items-center gap-1.5">
                //     <TbSubtask size={14} />
                //     Sub-Tasks
                //     <span style={{ fontSize: 11, color: taskUi.textFaint, fontVariantNumeric: "tabular-nums" }}>
                //       {siblings.length}
                //     </span>
                //   </span>
                // ),
                children: (
                  <div className="overflow-y-auto" style={{ maxHeight: "100%" }}>
                    {renderSubtasks(siblings)}
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    );
  };

  const rows: TemplateRow[] = [];

  template.groups.forEach((group) => {
    const groupId = String(group._id);
    const isCollapsed = collapsed.has(groupId);
    const rootCount = nodes.filter((n) => n.groupId === groupId && n.depth === 0).length;
    const subCount = nodes.filter((n) => n.groupId === groupId && n.depth === 1).length;

    rows.push({
      kind: "group",
      key: `group-${groupId}`,
      groupId,
      name: group.name,
      collapsed: isCollapsed,
      rootCount,
      subCount,
    });

    if (isCollapsed) return;

    const groupNodes = nodes.filter((node) => node.groupId === groupId && visibleIds.has(String(node.task._id)));

    if (groupNodes.length === 0) {
      rows.push({
        kind: "empty",
        key: `empty-${groupId}`,
        message: rootCount === 0 ? "No tasks in this group" : "Nothing matches the current filters",
      });
      return;
    }

    groupNodes.forEach((node) => rows.push({ kind: "task", key: String(node.task._id), node }));
  });

  const cellStyle = (row: TemplateRow): CSSProperties => {
    if (row.kind === "group") return { height: 34, background: GROUP_BG, borderBottom: `1px solid ${GRID_LINE}` };
    if (row.kind === "empty") return { background: "#ffffff", borderBottom: `1px solid ${taskUi.hairline}` };

    const taskId = String(row.node.task._id);
    const isSelected = taskId === selectedTaskId;
    const hasError = (issuesByTask.get(taskId) ?? []).some((issue) => issue.severity === "error");
    const isSub = row.node.depth === 1;

    return {
      height: isSub ? taskUi.subRowHeight : taskUi.rowHeight,
      background: isSelected ? "#f7f6fe" : hasError ? "#fef8f7" : isSub ? taskUi.subtle : "#ffffff",
      borderBottom: `1px solid ${taskUi.hairline}`,
    };
  };

  const spanCell = (row: TemplateRow, extra?: CSSProperties) => ({
    colSpan: row.kind === "task" ? 1 : 0,
    style: { ...cellStyle(row), ...extra },
  });

  const leadCell = (row: TemplateRow) => ({
    colSpan: row.kind === "task" ? 1 : COLUMN_COUNT,
    style: {
      ...cellStyle(row),
      ...(row.kind === "task" && String(row.node.task._id) === selectedTaskId
        ? { boxShadow: `inset 3px 0 0 ${taskUi.indigo}` }
        : {}),
    },
  });

  const renderGroupCell = (row: Extract<TemplateRow, { kind: "group" }>) => (
    <div className="flex h-full items-center" style={{ paddingLeft: 14 }}>
      <button
        type="button"
        onClick={() => toggleGroup(row.groupId)}
        aria-expanded={!row.collapsed}
        className="flex h-full flex-grow items-center gap-2 border-0 bg-transparent p-0 text-left"
        style={{ cursor: "pointer" }}
      >
        {row.collapsed ? <TbChevronRight size={13} color="#6b6b76" /> : <TbChevronDown size={13} color="#6b6b76" />}
        <Typography.Text style={{ fontSize: 12.5, fontWeight: 600, color: "#33333b" }}>{row.name}</Typography.Text>
        <Info className="ml-auto" tasks={row.rootCount} subtasks={row.subCount} />
      </button>
      <Button
        type="link"
        size="small"
        style={{ fontSize: 12, marginRight: 10 }}
        onClick={() => handleAddTask(row.groupId)}
      >
        Add task
      </Button>
    </div>
  );

  const renderLeadCell = (node: TaskNode) => {
    const taskId = String(node.task._id);
    const hasError = (issuesByTask.get(taskId) ?? []).some((issue) => issue.severity === "error");
    const isSelected = taskId === selectedTaskId;
    const isSub = node.depth === 1;

    return (
      <span
        className="flex h-full items-center gap-1.5"
        style={{
          paddingLeft: isSub ? 0 : 10,
          paddingRight: isSub ? 10 : 0,
          justifyContent: isSub ? "flex-end" : "flex-start",
        }}
      >
        {!isSub &&
          (hasError ? <TbAlertTriangle size={13} color="#b42318" /> : <TbGripVertical size={13} color="#c6c6d0" />)}
        <span
          style={{
            fontSize: isSub ? 10.5 : 11,
            fontFamily: "ui-monospace, Menlo, monospace",
            color: hasError ? "#b42318" : isSelected ? taskUi.indigo : "#8a8a96",
            fontWeight: hasError || isSelected ? 600 : 400,
          }}
        >
          {node.seq}
        </span>
      </span>
    );
  };

  const columns: ColumnsType<TemplateRow> = [
    {
      key: "lead",
      title: "",
      onHeaderCell: () => ({ style: headerCell }),
      onCell: (row) => leadCell(row),
      render: (_, row) => {
        if (row.kind === "group") return renderGroupCell(row);
        if (row.kind === "empty")
          return (
            <div style={{ padding: "12px 14px" }}>
              <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint }}>{row.message}</Typography.Text>
            </div>
          );
        return renderLeadCell(row.node);
      },
    },
    {
      key: "name",
      title: "Task",
      onHeaderCell: () => ({ style: headerCell }),
      onCell: (row) =>
        spanCell(
          row,
          row.kind === "task" && row.node.depth === 1 ? { borderLeft: `1px solid ${GRID_LINE}` } : undefined,
        ),
      render: (_, row) =>
        row.kind !== "task" ? null : (
          <span
            className="block truncate"
            style={{ fontSize: row.node.depth === 1 ? 13 : 13.5, paddingLeft: row.node.depth === 1 ? 14 : 0 }}
          >
            {row.node.task.name}
          </span>
        ),
    },
    {
      key: "code",
      title: "Code",
      onHeaderCell: () => ({ style: headerCell }),
      onCell: (row) => spanCell(row),
      render: (_, row) => {
        if (row.kind !== "task") return null;
        return <MonoCode value={row.node.task.code} tone="highlight" />;
      },
    },
    {
      key: "stage",
      title: "Stage",
      onHeaderCell: () => ({ style: headerCell }),
      onCell: (row) => spanCell(row),
      render: (_, row) => {
        if (row.kind !== "task") return null;
        const relatedStage = stages.find((s) => s._id === row.node.task.relatedStage);

        return relatedStage ? (
          <Tag bordered style={neutralTag}>
            <StageIcon iconTag={relatedStage.icon} color={resolveStageColor(relatedStage.color).text} />
            {relatedStage.label}
          </Tag>
        ) : (
          <span style={{ fontSize: 12.5, color: taskUi.textFaint }}>—</span>
        );
      },
    },
    {
      key: "deadline",
      title: "Deadline",
      onHeaderCell: () => ({ style: headerCell }),
      onCell: (row) => spanCell(row),
      render: (_, row) => {
        if (row.kind !== "task") return null;
        if (row.node.depth === 1) return <span style={{ fontSize: 12.5, color: "#c6c6d0" }}>—</span>;

        const deadline = row.node.task.deadline;

        return deadline ? (
          <Tag bordered style={neutralTag}>
            <TbClock size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
            {deadline.days}
            {deadline.businessDays ? "wd" : "d"}
          </Tag>
        ) : (
          <span style={{ fontSize: 12.5, color: taskUi.textFaint }}>—</span>
        );
      },
    },
    {
      key: "members",
      title: "Members",
      onHeaderCell: () => ({ style: headerCell }),
      onCell: (row) => spanCell(row),
      render: (_, row) =>
        row.kind !== "task" ? null : (
          <span className="flex items-center" style={{ paddingLeft: 5 }}>
            {ROLE_ORDER.map((role) => (
              <MemberDot
                key={role}
                role={role}
                member={memberFor(row.node.task, role)}
                label={memberLabel(memberFor(row.node.task, role))}
              />
            ))}
          </span>
        ),
    },
    {
      key: "actions",
      title: "",
      onHeaderCell: () => ({ style: headerCell }),
      onCell: (row) => spanCell(row),
      render: (_, row) =>
        row.kind !== "task" || row.node.depth === 1 ? null : (
          <span className="flex justify-center" role="presentation" onClick={(event) => event.stopPropagation()}>
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: "subtask",
                    icon: <TbPlus size={14} />,
                    label: "Add sub-task",
                    onClick: () => handleAddTask(row.node.groupId, { parent: row.node.task._id }),
                  },
                  {
                    key: "next",
                    icon: <TbPlus size={14} />,
                    label: "Add task after this",
                    onClick: () => handleAddTask(row.node.groupId, { previous: row.node.task._id }),
                  },
                  { type: "divider" },
                  { key: "duplicate", icon: <TbCopy size={14} />, label: "Duplicate", disabled: true },
                  { key: "delete", icon: <TbTrash size={14} />, label: "Delete", danger: true, disabled: true },
                ],
              }}
            >
              <Button
                type="text"
                size="small"
                aria-label="Task actions"
                icon={<TbDots size={15} />}
                style={{ color: "#b4b4be" }}
              />
            </Dropdown>
          </span>
        ),
    },
  ];

  return (
    <DashboardLayout
      headerRight={
        <Button type="primary" icon={<TbPlus size={15} />} onClick={() => setAddGroupOpen(true)}>
          Add group
        </Button>
      }
    >
      <PageTitle
        title={template.name}
        pageTitle={[
          { title: "Operations" },
          { title: "Task Templates", link: "/dashboard/operations/tasks" },
          { title: template.name },
        ]}
      />

      <div className="flex min-h-0 flex-1" style={{ background: taskUi.layout }}>
        <div className="flex min-w-0 flex-grow flex-col">
          <div className="flex flex-shrink-0 items-center gap-2.5" style={{ padding: "14px 20px" }}>
            <Input
              prefix={<TbSearch size={14} color="#9a9aa5" />}
              placeholder="Search tasks"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              className="w-80"
            />
            {/* <Select
              value={missingRole}
              onChange={setMissingRole}
              allowClear
              placeholder="Missing role"
              style={{ width: 170 }}
              options={ROLE_ORDER.map((role) => ({ value: role, label: `Missing ${ROLE_LABEL[role].toLowerCase()}` }))}
            />
            {issues.length > 0 && (
              <Button
                danger={!onlyIssues}
                type={onlyIssues ? "primary" : "default"}
                icon={<TbAlertTriangle size={14} />}
                onClick={() => setOnlyIssues((prev) => !prev)}
              >
                {errorCount > 0 ? `${errorCount} blocking` : `${issues.length} warnings`}
              </Button>
            )} */}

            <div className="flex-1" />

            <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint, fontVariantNumeric: "tabular-nums" }}>
              {template.groups.length} groups
              {" · "}
              <Info
                style={{ fontSize: 12.5 }}
                tasks={nodes.filter((node) => node.depth === 0).length}
                subtasks={countSubtasks(template.groups)}
              />
            </Typography.Text>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto" style={{ padding: "0 20px 20px" }}>
            {template.groups.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="A group holds an ordered chain of tasks — add one to start"
                style={{ paddingTop: 60 }}
              >
                <Button type="primary" icon={<TbPlus size={14} />} onClick={() => setAddGroupOpen(true)}>
                  Add first group
                </Button>
              </Empty>
            ) : (
              <div
                className="overflow-hidden border border-solid bg-white"
                style={{ borderColor: taskUi.border, borderRadius: taskUi.radius }}
              >
                <ConfigProvider
                  theme={{
                    components: {
                      Table: {
                        headerBg: taskUi.subtle,
                        headerColor: taskUi.textFaint,
                        headerSplitColor: "transparent",
                        headerBorderRadius: 0,
                        borderColor: taskUi.hairline,
                        rowHoverBg: "transparent",
                        cellPaddingBlock: 0,
                        cellPaddingInline: 0,
                      },
                    },
                  }}
                >
                  <Table<TemplateRow>
                    columns={columns}
                    dataSource={rows}
                    rowKey="key"
                    pagination={false}
                    onRow={(row) => {
                      if (row.kind !== "task") return {};
                      const taskId = String(row.node.task._id);

                      return {
                        tabIndex: 0,
                        "aria-selected": taskId === selectedTaskId,
                        style: { cursor: "pointer" },
                        onClick: () => selectTask(row.node.task),
                        onKeyDown: (event) => {
                          if (event.key !== "Enter" && event.key !== " ") return;
                          event.preventDefault();
                          selectTask(row.node.task);
                        },
                      };
                    }}
                  />
                </ConfigProvider>

                <Button
                  type="text"
                  className="w-full !p-3 !bg-transparent !rounded-none !text-start flex justify-start"
                  icon={<TbPlus size={15} />}
                  onClick={() => setAddGroupOpen(true)}
                  style={{ color: "#6b6b76", fontSize: 13, paddingLeft: 4 }}
                >
                  Add group
                </Button>
              </div>
            )}
          </div>
        </div>

        <div
          className="flex flex-shrink-0 flex-col border-0 border-l border-solid bg-white"
          style={{ width: 380, borderColor: taskUi.border }}
        >
          {renderInspector()}
        </div>
      </div>

      <AddGroupModal open={addGroupOpen} onClose={() => setAddGroupOpen(false)} templateId={id} />

      <AddTaskItemModal
        open={addTaskState !== null}
        onClose={() => setAddTaskState(null)}
        templateId={id}
        groupId={activeGroup?._id}
        groupTasks={activeGroup?.tasks ?? []}
        orgCode={orgCode}
        resource={template.resource}
        parent={addTaskState?.parent ?? null}
        previous={addTaskState?.previous ?? null}
      />
    </DashboardLayout>
  );
}

export default TaskTemplateDetailPage;
